import { Injectable, inject } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { GetTokenSilentlyVerboseResponse } from '@auth0/auth0-spa-js';
import { EMPTY, Subscription, firstValueFrom, from, of, timer } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { SESSION_REFRESH_BEFORE_MS } from 'src/app/core/constants/session.constants';
import { environment } from 'src/environments/environment';
import { InactivityStateService } from './inactivity-state.service';

@Injectable({ providedIn: 'root' })
export class TokenRefreshService {
  private readonly auth = inject(AuthService);
  private readonly inactivityState = inject(InactivityStateService);

  private refreshSubscription?: Subscription;

  constructor() {
    this.auth.isAuthenticated$
      .pipe(
        switchMap((isAuthenticated) => {
          if (!isAuthenticated) {
            this.refreshSubscription?.unsubscribe();
            this.refreshSubscription = undefined;
            return EMPTY;
          }

          return from(this.getVerifiedTokenResponse()).pipe(
            tap((response) => {
              this.scheduleRefresh(response);
            }),
            catchError(() => EMPTY),
          );
        }),
      )
      .subscribe();
  }

  /** Silently refreshes the Auth0 access token and reschedules the next refresh. */
  refreshSessionToken(): Promise<string | null> {
    return firstValueFrom(
      from(this.getVerifiedTokenResponse()).pipe(
        tap((response) => {
          this.scheduleRefresh(response);
        }),
        map((response) => response.access_token),
        catchError(() => of<string | null>(null)),
      ),
    );
  }

  scheduleRefresh(response: GetTokenSilentlyVerboseResponse): void {
    const refreshIn = response.expires_in * 1000 - SESSION_REFRESH_BEFORE_MS;
    if (!Number.isFinite(refreshIn) || refreshIn <= 0) {
      return;
    }

    this.refreshSubscription?.unsubscribe();
    this.refreshSubscription = timer(refreshIn).subscribe(() => {
      this.refreshIfActive();
    });
  }

  private refreshIfActive(): void {
    if (this.inactivityState.getValue() !== 'active') {
      return;
    }

    from(this.getVerifiedTokenResponse())
      .pipe(
        tap((response) => {
          this.scheduleRefresh(response);
        }),
        catchError(() => EMPTY),
      )
      .subscribe();
  }

  /** Uses Auth0's verified token response instead of manually decoding JWT payloads. */
  private getVerifiedTokenResponse(): Promise<GetTokenSilentlyVerboseResponse> {
    return firstValueFrom(
      this.auth.getAccessTokenSilently({
        authorizationParams: {
          audience: environment.auth0.audience,
        },
        detailedResponse: true,
      }),
    );
  }
}
